import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  UpdateInternalAuditInput,
  UpdateInternalAuditMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateInternalAuditDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * tRPC input type for updateInternalAudit. Carries OriginalTimestamp for
 * optimistic concurrency; a stale timestamp surfaces as a TRPCClientError with
 * code CONFLICT. BusinessArea is free-text (resolved server-side); the legacy
 * BusinessAreaId is accepted but re-derived by the server.
 */
type UpdateInternalAuditTRPCInput = {
  Id: string;
  Title: string;
  OriginalTimestamp: string;
  Description?: string | null;
  BusinessArea?: string | null;
  BusinessAreaId?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

export const useUpdateInternalAudit = createMutationHook({
  name: 'updateInternalAudit',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.internalAuditEntity.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateInternalAuditInput
  ): UpdateInternalAuditTRPCInput => ({
    Id: variables.Id,
    Title: variables.Title ?? '',
    OriginalTimestamp: variables.OriginalTimestamp,
    Description: variables.Description,
    BusinessArea: variables.BusinessArea,
    BusinessAreaId: variables.BusinessAreaId,
    CustomAttributeData: variables.CustomAttributeData,
    OwnerUserIds: variables.OwnerUserIds ?? [],
    OwnerGroupIds: variables.OwnerGroupIds ?? [],
    ContributorUserIds: variables.ContributorUserIds ?? [],
    ContributorGroupIds: variables.ContributorGroupIds ?? [],
    ViewerUserIds: variables.ViewerUserIds,
    ViewerGroupIds: variables.ViewerGroupIds,
    TagTypeIds: variables.TagTypeIds ?? [],
    DepartmentTypeIds: variables.DepartmentTypeIds ?? [],
  }),
  invalidate: (trpc) => [
    trpc.frontend.internalAuditEntity.register.queryKey(),
    trpc.frontend.internalAuditEntity.internalAuditById.queryKey(),
    // The update find-or-creates a business_area for a new free-text title
    // and runs the org-scoped orphan cleanup server-side, so the
    // business-area list can change either way — refresh it (parity with
    // the Apollo path's evictField(cache, 'business_area')).
    trpc.frontend.businessArea.businessAreas.queryKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateInternalAuditMutation => ({
    updateInternalAudit: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateInternalAuditDocument,
  graphqlVariables: (variables: UpdateInternalAuditInput) => ({
    Input: variables,
  }),
  evictFields: ['internal_audit_entity', 'business_area'],
});
